import { ContentAnchor, LogoPosition } from '@api/selections';
import { BlockComponentBaseProps, EpiImage, EpiLink } from '@api/types';
import {
  BackgroundImage,
  BlockContainer,
  Button,
  EditPanel,
  Html,
  Image,
  SmartLink,
  Text,
  Title,
} from '@components';
import { Box, Typography, styled } from '@mui/material';
import { breakpoints, palette } from '@themes/theme.helpers';
import { formatDateRange } from '@utils/date.utils';
import classnames from 'classnames';

// STYLES
//--------
const CampaignWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'imageAnchor',
})<CampaignBlockProps>`
  * {
    box-sizing: border-box;
  }
  text-align: left;
  color: ${palette.text('primary')};
  display: flex;
  align-items: stretch;
  position: relative;
  ${breakpoints.up('sm')} {
    min-height: 240px;
  }

  ${breakpoints.up('md')} {
    min-height: 360px;
  }

  ${breakpoints.up('lg')} {
    min-height: 480px;
  }

  .epi-background-image {
    background-size: cover;
    display: flex;
    align-items: stretch;
    background-position: ${(p) => p.imageAnchor} !important;
    flex-direction: column;
    width: 100%;
    ${breakpoints.up('lg')} {
      flex-direction: row;
    }
  }

  &.left .epi-background-image {
    justify-content: flex-end;
  }

  &.right .epi-background-image {
    justify-content: flex-start;
  }

  .epi-image {
    display: block;
    ${breakpoints.up('lg')} {
      display: none;
    }
  }

  .main-title {
    margin-bottom: 16px;
    ${breakpoints.up('md')} {
      margin-bottom: 20px;
    }
  }
`;

const CampaignContent = styled(Box)`
  color: ${palette.text('primary')};
  background: ${palette.background('paper')};
  padding: 20px;

  ${breakpoints.up('lg')} {
    background: #ffffffb3;
    width: 50%;
  }

  &.left {
    ${breakpoints.up('lg')} {
      padding: 30px 120px 30px 80px;
    }
  }

  &.right {
    ${breakpoints.up('lg')} {
      padding: 30px 80px 30px 120px;
    }
  }

  .campaign-cta-button {
    display: block;
    margin-top: 32px;
  }
`;

const LogoImage = styled(Box)<{ logowidth?: number }>`
  position: absolute;
  width: ${(props) => (props.logowidth ? `${props.logowidth}px` : '100px')};
  height: auto;
  z-index: 1;

  &.top-left {
    top: 10px;
    left: 10px;
    ${breakpoints.up('md')} {
      top: 38px;
      left: 38px;
    }
  }

  &.top-right {
    top: 10px;
    right: 10px;
    ${breakpoints.up('md')} {
      top: 38px;
      right: 38px;
    }
  }

  &.bottom-left {
    bottom: 10px;
    left: 10px;
    ${breakpoints.up('md')} {
      bottom: 38px;
      left: 38px;
    }
  }

  &.bottom-right {
    bottom: 10px;
    right: 10px;
    ${breakpoints.up('md')} {
      bottom: 38px;
      right: 38px;
    }
  }
`;

// TYPES
//-------
export type CampaignBlockProps = BlockComponentBaseProps & {
  title?: string;
  text?: string;
  image?: EpiImage;
  imagePlacement?: 'left' | 'right';
  imageAnchor?: ContentAnchor;
  campaignStartDate?: string;
  campaignEndDate?: string;
  link?: EpiLink;
  linkCaption?: string;
  logoWidth?: number;
  logo?: EpiImage;
  logoPosition?: LogoPosition;
};

// COMPONENTS
//------------
export const CampaignBlock = ({
  title,
  text,
  image,
  imagePlacement,
  imageAnchor,
  campaignStartDate,
  campaignEndDate,
  link,
  linkCaption,
  disableEditPanel = false,
  logoPosition,
  logo,
  logoWidth,
}: CampaignBlockProps) => {
  const logoUrl = typeof logo === 'string' ? logo : logo?.url;
  const logoClass = classnames('campaign-block-logo', logoPosition || 'top-right');
  const mainClassName = classnames('campaign-block', imagePlacement);
  return (
    <BlockContainer>
      <EditPanel
        disableEditPanel={disableEditPanel}
        properties={[
          'title',
          'text',
          'image',
          'imagePlacement',
          'imageAnchor',
          'campaignStartDate',
          'campaignEndDate',
          'link',
          'linkCaption',
        ]}
      />
      <CampaignWrapper className={mainClassName} imageAnchor={imageAnchor}>
        {logoUrl && (
          <LogoImage className={logoClass} logowidth={logoWidth}>
            <img src={logoUrl} alt='logo' />
          </LogoImage>
        )}
        <BackgroundImage image={image}>
          <Image image={image} />
          <CampaignContent className={imagePlacement}>
            <Text sx={{ mb: 3, fontWeight: '700 !important' }}>
              {formatDateRange(campaignStartDate, campaignEndDate)}
            </Text>
            <Title variant='h3' noDot>
              {title}
            </Title>
            <Typography variant='body1'>
              <Html html={text}></Html>
            </Typography>
            {link && (
              <SmartLink {...link} className='campaign-cta-button'>
                <Button variant='primary'>{linkCaption || link?.text}</Button>
              </SmartLink>
            )}
          </CampaignContent>
        </BackgroundImage>
      </CampaignWrapper>
    </BlockContainer>
  );
};
